import React, { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface EditableTextProps {
  value: string | number;
  onSave: (newValue: string) => void;
  isEditingMode?: boolean;
  label?: string;
  multiline?: boolean;
  type?: 'text' | 'number';
  className?: string;
  children: React.ReactNode;
}

export function EditableText({
  value,
  onSave,
  isEditingMode = false,
  label = 'Texte',
  multiline = false,
  type = 'text',
  className = '',
  children
}: EditableTextProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));

  React.useEffect(() => {
    setDraft(String(value ?? ''));
  }, [value]);

  if (!isEditingMode) {
    return <>{children}</>;
  }

  const handleConfirm = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    onSave(draft);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDraft(String(value ?? ''));
    setIsOpen(false);
  };

  return (
    <span className={`relative inline-block group/edit ${className}`}>
      <span
        onClick={() => setIsOpen(true)}
        className="relative inline-block cursor-pointer transition-all duration-200 rounded p-1 -m-1 ring-1 ring-amber-400/60 hover:ring-2 hover:ring-amber-400 hover:bg-amber-400/10"
        title={`Cliquer pour modifier "${label}" en direct`}
      >
        {children}
        <span className="absolute -top-2 -right-2 z-30 bg-amber-400 text-black p-1 rounded-full shadow-lg opacity-90 group-hover/edit:scale-110 transition-transform flex items-center justify-center pointer-events-none">
          <Pencil className="w-2.5 h-2.5" />
        </span>
      </span>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
            onClick={handleCancel}
          >
            <div
              className="bg-zinc-900 border border-amber-400/50 shadow-2xl p-5 max-w-lg w-full text-left font-sans relative text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-4">
                <div className="flex items-center gap-2 text-amber-400 font-mono text-xs uppercase tracking-wider font-bold">
                  <Pencil className="w-3.5 h-3.5" />
                  <span>Édition en direct : {label}</span>
                </div>
                <button
                  onClick={handleCancel}
                  className="text-zinc-400 hover:text-white p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleConfirm} className="space-y-4">
                {multiline ? (
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={4}
                    className="w-full bg-black border border-white/20 p-3 text-sm text-white focus:border-amber-400 outline-none rounded-none font-sans"
                    autoFocus
                  />
                ) : (
                  <input
                    type={type}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full bg-black border border-white/20 p-3 text-sm text-white focus:border-amber-400 outline-none rounded-none font-sans"
                    autoFocus
                  />
                )}

                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <p className="text-[10px] text-zinc-400 font-mono">
                    {multiline ? 'Appuyez sur Enregistrer pour valider' : 'Entrée pour enregistrer'}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 border border-white/20 text-xs uppercase tracking-wider text-zinc-300 hover:bg-white/10 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 bg-amber-400 text-black font-bold text-xs uppercase tracking-wider hover:bg-amber-300 transition-colors flex items-center gap-1.5"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Enregistrer
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
